import React, { useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { ShiftFields } from "./ShiftCard";

const hm = (iso) => (iso ? iso.slice(11, 16) : "");

export default function ReportEditDialog({ report, needReason, onClose, onSaved }) {
  const { t, tn } = useI18n();
  const [workDate, setWorkDate] = useState(report.work_date || report.date);
  const [comment, setComment] = useState(report.comment || "");
  const [reason, setReason] = useState("");
  const [items, setItems] = useState(report.items.map((it) => ({ ...it })));
  const [shift, setShift] = useState(report.shift ? { start: hm(report.shift.start_time), end: hm(report.shift.end_time), break_minutes: report.shift.break_minutes ?? 0 } : { start: "", end: "", break_minutes: "" });
  const [busy, setBusy] = useState(false);

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

  const save = async () => {
    if (needReason && !reason.trim()) return toast.error(t("reason_required"));
    if (items.length === 0) return toast.error(t("no_data"));
    if (items.some((it) => !(Number(it.actual_minutes) > 0) || !(Number(it.quantity) > 0))) return toast.error(t("actual_required"));
    setBusy(true);
    try {
      const body = {
        work_date: workDate, comment, reason: reason.trim(),
        items: items.map((it) => ({ id: it.id, work_type_id: it.work_type_id, work_group_id: it.work_group_id, work_subgroup_id: it.work_subgroup_id,
          unit_id: it.unit_id, quantity: Number(it.quantity), actual_minutes: Number(it.actual_minutes), comment: it.comment || "", photo_ids: it.photo_ids || [] })),
      };
      if (shift.start && shift.end) body.shift = { start: shift.start, end: shift.end, break_minutes: Number(shift.break_minutes) || 0 };
      await api.put(`/reports/${report.id}`, body);
      toast.success(t("updated")); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("edit_report")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("work_date")}</label>
            <Input data-testid="edit-work-date" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} className="h-10" />
          </div>
          <div className="tactical-card p-3">
            <div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("shift")}</div>
            <ShiftFields value={shift} onChange={setShift} compact />
          </div>
          <div className="space-y-2" data-testid="edit-items">
            {items.map((it, i) => (
              <div key={it.id || i} className="tactical-card p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  {it.item_number && <span className="text-xs text-[var(--text-3)] font-mono-num shrink-0">{it.item_number}</span>}
                  <span className="flex-1 min-w-0 font-semibold break-words">{tn(it.work_name)}</span>
                  <button data-testid={`edit-del-item-${i}`} onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-1"><Trash2 size={15} className="text-[var(--red)]" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("quantity")} · {tn(it.unit_name)}</label>
                    <Input data-testid={`edit-qty-${i}`} type="number" inputMode="decimal" value={it.quantity} disabled={it.measurement_type === "TIME_BASED"} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10 font-mono-num" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase font-semibold text-[var(--text-3)]">{t("actual_minutes")}</label>
                    <Input data-testid={`edit-min-${i}`} type="number" inputMode="numeric" value={it.actual_minutes ?? ""} onChange={(e) => setItem(i, "actual_minutes", e.target.value)} className="h-10 font-mono-num" />
                  </div>
                </div>
                <Input data-testid={`edit-comment-${i}`} placeholder={t("comment")} value={it.comment || ""} onChange={(e) => setItem(i, "comment", e.target.value)} className="h-9 text-sm" />
              </div>
            ))}
          </div>
          <Textarea data-testid="edit-report-comment" placeholder={t("comment")} value={comment} onChange={(e) => setComment(e.target.value)} />
          {needReason && <Input data-testid="edit-reason" placeholder={t("reason") + " *"} value={reason} onChange={(e) => setReason(e.target.value)} className="h-10" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="edit-save-btn" onClick={save} disabled={busy} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export async function deleteReport(report, needReason, t) {
  let reason = "";
  if (needReason) {
    reason = window.prompt(t("reason") + " *") || "";
    if (!reason.trim()) { toast.error(t("reason_required")); return false; }
  } else if (!window.confirm(t("delete_confirm"))) return false;
  try {
    await api.delete(`/reports/${report.id}?reason=${encodeURIComponent(reason)}`);
    toast.success(t("deleted")); return true;
  } catch (e) { toast.error(e.response?.data?.detail || t("error")); return false; }
}
